import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [brandFilter, setBrandFilter] = useState("All");

  const [deleteTarget, setDeleteTarget] = useState(null); // produk yang akan dihapus (untuk modal konfirmasi)
  const [deleting, setDeleting] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError("");
    const [
      { data: productsData, error: productsErr },
      { data: categoriesData },
      { data: brandsData },
    ] = await Promise.all([
      supabase.from("products").select("*").order("name"),
      supabase.from("categories").select("*").order("sort_order"),
      supabase.from("brands").select("*").order("sort_order"),
    ]);

    if (productsErr) {
      setError(productsErr.message);
    } else {
      setProducts(productsData || []);
    }
    setCategories(categoriesData || []);
    setBrands(brandsData || []);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return products.filter((p) => {
      const matchSearch = !q || p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q);
      const matchCategory = categoryFilter === "All" || p.category_id === categoryFilter;
      const matchBrand = brandFilter === "All" || p.brand_id === brandFilter;
      return matchSearch && matchCategory && matchBrand;
    });
  }, [products, search, categoryFilter, brandFilter]);

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error: delErr } = await supabase.from("products").delete().eq("id", deleteTarget.id);
    setDeleting(false);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setProducts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const toggleActive = async (product) => {
    const { error: updErr } = await supabase
      .from("products")
      .update({ is_active: !product.is_active })
      .eq("id", product.id);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setProducts((prev) =>
      prev.map((p) => (p.id === product.id ? { ...p, is_active: !p.is_active } : p))
    );
  };

  return (
    <div style={{ padding: "24px 28px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Produk</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>{products.length} total produk dalam katalog</div>
        </div>
        <Link
          to="/admin/products/new"
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            background: "#3b82f6", color: "#fff", borderRadius: 8, fontSize: 13,
            fontWeight: 600, textDecoration: "none",
          }}
        >
          + Tambah Produk
        </Link>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama atau ID produk..."
          style={{
            flex: "1 1 240px", padding: "9px 12px", borderRadius: 8,
            border: "1px solid #cbd5e1", fontSize: 13, outline: "none",
          }}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
        >
          <option value="All">Semua Kategori</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <select
          value={brandFilter}
          onChange={(e) => setBrandFilter(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
        >
          <option value="All">Semua Brand</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>{b.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
          fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Memuat produk...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Tidak ada produk ditemukan.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={thStyle}>Produk</th>
                <th style={thStyle}>Kategori</th>
                <th style={thStyle}>Brand</th>
                <th style={thStyle}>Dimensi (W×H×D)</th>
                <th style={thStyle}>Mount</th>
                <th style={thStyle}>Coverage</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 6, background: "#f1f5f9",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, overflow: "hidden",
                      }}>
                        {p.image_url ? (
                          <img src={p.image_url} alt="" style={{ width: "75%", height: "75%", objectFit: "contain" }} />
                        ) : (
                          <span style={{ fontSize: 10, color: "#cbd5e1" }}>?</span>
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>{p.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>{p.category_id}</td>
                  <td style={tdStyle}>{p.brand_id}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12 }}>
                    {p.width}×{p.height}×{p.depth}m
                  </td>
                  <td style={{ ...tdStyle, textTransform: "capitalize" }}>{p.mount_on}</td>
                  <td style={tdStyle}>
                    {p.coverage_angle >= 300 ? "360°" : `${p.coverage_angle}°`} · {p.coverage_distance}m
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => toggleActive(p)}
                      style={{
                        padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                        border: "none", cursor: "pointer",
                        background: p.is_active ? "#dcfce7" : "#f1f5f9",
                        color: p.is_active ? "#166534" : "#64748b",
                      }}
                    >
                      {p.is_active ? "Aktif" : "Nonaktif"}
                    </button>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <Link
                      to={`/admin/products/${p.id}`}
                      style={{ fontSize: 12, fontWeight: 600, color: "#3b82f6", textDecoration: "none", marginRight: 14 }}
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => setDeleteTarget(p)}
                      style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal konfirmasi hapus */}
      {deleteTarget && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 360 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
              Hapus produk?
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
              <strong>{deleteTarget.name}</strong> akan dihapus permanen dari katalog. Tindakan ini tidak bisa dibatalkan.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Batal
              </button>
              <button
                onClick={handleDeleteConfirmed}
                disabled={deleting}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {deleting ? "Menghapus..." : "Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 };
const tdStyle = { padding: "10px 14px", color: "#334155" };
